import React from 'react';
import { Box, Button, IconButton, BoxProps } from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

interface FloatingActionButtonProps {
  onClick: () => void;
  icon?: React.ReactElement;
  label?: string;
  colorScheme?: string;
  size?: string;
  position?: {
    bottom?: BoxProps['bottom'];
    right?: BoxProps['right'];
  };
  showOnDesktop?: boolean;
}

/**
 * 浮动操作按钮组件
 *
 * @param onClick 点击按钮时的回调函数
 * @param icon 按钮图标，默认为加号图标
 * @param label 按钮文字（如果提供，则显示文字按钮而非图标按钮）
 * @param colorScheme 按钮颜色方案，默认为teal
 * @param size 按钮大小，默认为md
 * @param position 位置设置，可自定义bottom和right值
 * @param showOnDesktop 是否在桌面端显示，默认仅在移动端显示
 */
export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onClick,
  icon = <MdAdd size={24} />,
  label,
  colorScheme = 'teal',
  size = 'lg',
  position = { bottom: '80px', right: '20px' },
  showOnDesktop = false,
}) => {
  const { t } = useTranslation(['v0/common']);

  return (
    <Box
      position="fixed"
      bottom={position.bottom}
      right={position.right}
      display={{ base: 'block', md: showOnDesktop ? 'block' : 'none' }}
      zIndex={10}
    >
      {label ? (
        <Button
          leftIcon={icon}
          colorScheme={colorScheme}
          onClick={onClick}
          size={size}
          borderRadius="full"
          boxShadow="lg"
          px={6}
          _hover={{ transform: 'scale(1.05)' }}
        >
          {label}
        </Button>
      ) : (
        <IconButton
          aria-label={t('floatingButton.ariaLabel')}
          icon={icon}
          colorScheme={colorScheme}
          size={size}
          isRound
          w={{ base: '60px', md: '60px' }}
          h={{ base: '60px', md: '60px' }}
          boxShadow="lg"
          onClick={onClick}
          _hover={{ transform: 'scale(1.05)' }}
        />
      )}
    </Box>
  );
};